"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";
import { PageHero } from "../components/PageHero";
import { APP_STORES, detectPlatform, downloadTarget, storeUrl, type DevicePlatform, type StoreConfig } from "../lib/appStores";

const subscribe = () => () => {};
function browserPlatform(): DevicePlatform {
  try { return detectPlatform(navigator.userAgent, navigator.maxTouchPoints); }
  catch { return "unknown"; }
}
function browserSearch() {
  try { return window.location.search; }
  catch { return ""; }
}

export function DownloadExperience({ config = APP_STORES }: { config?: StoreConfig }) {
  const platform = useSyncExternalStore(subscribe, browserPlatform, () => "unknown");
  const search = useSyncExternalStore(subscribe, browserSearch, () => "");
  const attempted = useRef(false);
  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    try {
      const target = downloadTarget(browserPlatform(), browserSearch(), config);
      if (target) window.location.replace(target);
    } catch {
      // Detection/navigation may be restricted in embedded browsers. Links remain usable.
    }
  }, [config]);

  const mobile = platform === "ios" || platform === "android";
  const unavailable = mobile && !storeUrl(platform, config);
  return <>
    <PageHero eyebrow="Meet with intention" title={unavailable ? "Coming soon for your device" : "Download Vowent on your phone."}
      intro={unavailable ? "Vowent isn’t available for your device just yet. We’ll share the download here when it’s ready." : "Dating should lead somewhere. Find Vowent in your phone’s app store."} />
    <section className="site-container py-12 lg:py-16" aria-label="Download options">
      <p className="mb-6 text-slate" role="status">{mobile && !unavailable ? "Opening your app store. If it doesn’t open, use the link below." : "For adults 21+. Meet with intention. Build something meaningful."}</p>
      <div className="grid max-w-3xl gap-5 sm:grid-cols-2">
        {(["ios", "android"] as const).map(device => {
          const url = downloadTarget(device, search, config);
          const name = device === "ios" ? "App Store" : "Google Play";
          return <div key={device} className={`rounded-2xl border border-hairline p-7 ${url ? "bg-mist" : "bg-paper"}`}>
            <p className="text-sm text-slate">{device === "ios" ? "iPhone & iPad" : "Android"}</p>
            <h2 className="mt-2 font-display text-2xl font-semibold">{name}</h2>
            {url ? <a href={url} className="mt-6 inline-flex min-h-12 items-center rounded-full bg-teal-700 px-6 py-3 text-sm font-semibold text-white hover:bg-teal-800">{`Download on ${name}`}</a>
              : <p className="mt-6 inline-flex min-h-12 items-center rounded-full bg-mist px-6 py-3 text-sm text-slate">Coming soon</p>}
          </div>;
        })}
      </div>
      <noscript><p className="mt-6 text-slate">Choose your phone’s app store above to download Vowent.</p></noscript>
    </section>
  </>;
}
